import React, { useState, useEffect, useMemo } from 'react';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useArticleCategories, useCreateArticleCategory, useUpdateArticleCategory } from '@/hooks/useArticles';
import { useArticleCategoryDialog } from '@/contexts/ArticleCategoryContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { UnifiedLLMService } from '@/services/llm/UnifiedLLMService';
import { ApiCallsAuditService } from '@/services/apiCallsAuditService';
import type { ArticleCategory } from '@/types/articles';

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

// Get all descendant IDs to prevent circular references
const getDescendantIds = (categories: ArticleCategory[], parentId: string): string[] => {
  const descendants: string[] = [];
  const children = categories.filter(c => c.parent_id === parentId);
  
  for (const child of children) {
    descendants.push(child.id);
    descendants.push(...getDescendantIds(categories, child.id));
  }
  
  return descendants;
};

export const GlobalArticleCategoryDialog: React.FC = () => {
  const { currentBrandId: selectedBrandId } = useBrandManager();
  const { data: categories = [] } = useArticleCategories(selectedBrandId);
  const createCategory = useCreateArticleCategory();
  const updateCategory = useUpdateArticleCategory();
  const { isOpen, editingCategory, closeCategoryDialog } = useArticleCategoryDialog();

  // Form state persists across tab changes
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [parentId, setParentId] = useState<string | null>(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Sync form when editing category changes
  useEffect(() => {
    if (editingCategory) {
      setName(editingCategory.name);
      setSlug(editingCategory.slug);
      setDescription(editingCategory.description || '');
      setColor(editingCategory.color);
      setParentId(editingCategory.parent_id);
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setColor(COLORS[0]);
      setParentId(null);
    }
    setSuggestions([]);
  }, [editingCategory, isOpen]);

  // Get valid parent options (exclude self and descendants)
  const validParentOptions = useMemo(() => {
    if (!editingCategory) return categories;
    const excludeIds = new Set([editingCategory.id, ...getDescendantIds(categories, editingCategory.id)]);
    return categories.filter(c => !excludeIds.has(c.id));
  }, [categories, editingCategory]);

  const resetForm = () => {
    setName('');
    setSlug('');
    setDescription('');
    setColor(COLORS[0]);
    setParentId(null);
    setSuggestions([]);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      resetForm();
      closeCategoryDialog();
    }
  };

  const handleSave = async () => {
    if (!selectedBrandId || !name.trim()) {
      toast.error('Name is required');
      return;
    }

    const categorySlug = slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          name,
          slug: categorySlug,
          description: description || null,
          color,
          parent_id: parentId,
        });
        toast.success('Category updated');
      } else {
        await createCategory.mutateAsync({
          brand_id: selectedBrandId,
          name,
          slug: categorySlug,
          description: description || null,
          color,
          parent_id: parentId,
        });
        toast.success('Category created');
      }
      resetForm();
      closeCategoryDialog();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleGenerateDescription = async () => {
    if (!name.trim()) {
      toast.error('Please enter a category name first');
      return;
    }

    if (!selectedBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingDescription(true);
    setSuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: selectedBrandId,
        source: 'article_category_description',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful SEO and content expert. Generate exactly 3 unique, SEO-friendly descriptions for article categories. Each description should be 1-2 sentences, around 120-160 characters, and highlight the value of the category content. Return ONLY a JSON array with 3 strings, no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different SEO-friendly descriptions for an article category named "${name}". Return as a JSON array of 3 strings.`
          }
        ],
        max_tokens: 500
      });

      // Log the API call
      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'article_category_description',
        platform: (response.provider as any) || 'openai',
        brand_id: selectedBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { category_name: name }
      });

      // Parse the response
      const content = response.content.trim();
      let parsed: string[] = [];
      
      try {
        // Try to extract JSON array from response
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          // Fallback: split by newlines if no JSON array
          parsed = content.split('\n').filter(line => line.trim()).slice(0, 3);
        }
      } catch {
        // Fallback to single description
        parsed = [content];
      }

      if (parsed.length > 0) {
        setSuggestions(parsed.slice(0, 3));
        toast.success('Descriptions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Failed to generate descriptions');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setDescription(suggestion);
    setSuggestions([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={editingCategory ? 'Edit Article Category' : 'New Article Category'}
            popupContext="/articles/categories"
            isOpen={isOpen}
          >
            {editingCategory ? 'Edit Category' : 'New Category'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Parent Category</Label>
            <Select
              value={parentId || 'none'}
              onValueChange={(value) => setParentId(value === 'none' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select parent category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No parent (top-level)</SelectItem>
                {validParentOptions.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="Category name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Slug</Label>
            <Input
              placeholder="category-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
            <p className="text-xs text-muted-foreground">Leave empty to auto-generate from name</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={handleGenerateDescription}
                disabled={isGeneratingDescription || !name.trim()}
                className="h-7 px-2 text-xs"
              >
                {isGeneratingDescription ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1" />
                )}
                AI Suggest
              </Button>
            </div>
            <Textarea
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
            {suggestions.length > 0 && (
              <div className="space-y-2 mt-2">
                <p className="text-xs text-muted-foreground">Click to select:</p>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left text-sm p-2 rounded-md border bg-muted/50 hover:bg-muted transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogClose(false)}>Cancel</Button>
          <Button onClick={handleSave}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
